import { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";

interface Props {
  /** Which roles unlock the app. For the claimant SWA, all three. For admin, just approver + admin. */
  allow: Array<"claimant" | "approver" | "admin">;
  children: ReactNode;
}

export function AccessGate({ allow, children }: Props) {
  const me = useQuery({ queryKey: ["me-gate"], queryFn: api.me, retry: 1, staleTime: 60000 });
  const { instance } = useMsal();

  if (me.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading...
      </div>
    );
  }

  if (me.error) {
    return (
      <Denied
        title="Sign-in problem"
        body={`Could not verify your access. ${(me.error as Error).message}`}
        onSignOut={() => instance.logoutRedirect()}
      />
    );
  }

  const roles = me.data?.roles ?? {};
  const granted = allow.some((r) => (roles as any)[r]);
  if (!granted) {
    return (
      <Denied
        title="You do not have access"
        body={
          <>
            Your Microsoft account is signed in but you are not a member of the right
            Entra security group yet. Please contact IT and ask to be added to one of:
            <ul className="mt-2 list-disc list-inside text-slate-700">
              {allow.includes("claimant") && <li>Liaise Pricing - Claimants</li>}
              {allow.includes("approver") && <li>Liaise Pricing - Approvers</li>}
              {allow.includes("admin")    && <li>Liaise Pricing - Admins</li>}
            </ul>
            <p className="mt-3">Already added? Sign out and back in to refresh your token.</p>
          </>
        }
        onSignOut={() => instance.logoutRedirect()}
      />
    );
  }

  return <>{children}</>;
}

function Denied({ title, body, onSignOut }: { title: string; body: ReactNode; onSignOut: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-4">
          <img src="/liaise-wordmark-navy.svg" alt="Liaise" className="h-7 w-auto" />
          <span className="text-slate-400">|</span>
          <span className="font-medium text-liaise-navy">Pricing</span>
        </div>
        <h1 className="text-xl font-semibold text-liaise-navy mb-2">{title}</h1>
        <div className="text-sm text-slate-600">{body}</div>
        <button
          onClick={onSignOut}
          className="mt-6 px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
